import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Drawer,
  Divider,
  CircularProgress,
  Chip,
  Stack,
  Collapse,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { ClaimDetailDto, ClaimSummaryDto } from '@common/synapse/introspection-types';
import { DIMENSION_LABELS } from '@common/synapse/introspection-types';
import ConfidenceTrajectoryChart from './ConfidenceTrajectoryChart';
import ClaimCurationPanel from './ClaimCurationPanel';
import ClaimEditHistoryList from './ClaimEditHistoryList';

function ProvenanceChip({ provenance }: { provenance: ClaimSummaryDto['provenance'] }) {
  const color =
    provenance === 'manual' ? 'secondary' : provenance === 'edited' ? 'warning' : provenance === 'retracted' ? 'default' : 'success';
  return <Chip size="small" label={provenance} color={color} variant="outlined" />;
}

const CURATION_EVENT_TYPES = new Set([
  'claim.corrected',
  'claim.retracted',
  'claim.restored',
  'claim.manual_added',
]);

const LIFE_EVENT_LABELS: Record<string, string> = {
  created: 'Created',
  reinforced: 'Reinforced',
  weakened: 'Weakened',
  adjusted: 'Adjusted',
  contradiction: 'Contradiction',
  superseded: 'Superseded',
  scope_shift: 'Scope shift',
  edited: 'Edited',
  retracted: 'Retracted',
};

function SectionHeader({
  title,
  expanded,
  onToggle,
  first = false,
  nested = false,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  first?: boolean;
  nested?: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mt: first ? (nested ? 0.5 : 1) : nested ? 1.5 : 2, mb: 0.5, cursor: 'pointer' }}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <Typography variant={nested ? 'caption' : 'subtitle2'} color={nested ? 'text.secondary' : 'text.primary'}>
        {title}
      </Typography>
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        aria-label={expanded ? 'Collapse section' : 'Expand section'}
      >
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
    </Stack>
  );
}

function CollapsibleSection({
  title,
  expanded,
  onToggle,
  children,
  first = false,
  nested = false,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  first?: boolean;
  nested?: boolean;
}) {
  return (
    <Box>
      <SectionHeader title={title} expanded={expanded} onToggle={onToggle} first={first} nested={nested} />
      <Collapse in={expanded}>{children}</Collapse>
    </Box>
  );
}

export interface ClaimDetailDrawerProps {
  open: boolean;
  detail: ClaimDetailDto | null;
  userId?: string;
  accessToken?: string;
  clientDemoMode?: boolean;
  onClose: () => void;
  onOpenClaim?: (claimId: string) => void;
  onViewInConversation?: (chatId: string) => void;
  onClaimUpdated?: () => void;
}

function normalizeClaimDetail(detail: ClaimDetailDto): ClaimDetailDto {
  const evidenceQuotes = detail.evidenceQuotes ?? [];
  const evidenceDetails =
    detail.evidenceDetails ??
    evidenceQuotes.map((quote) => ({
      quote,
      spanRole: 'primary',
      conversationId: detail.sourceContext?.chatId ?? '',
      turnIds: detail.sourceTurnId ? [detail.sourceTurnId] : [],
    }));

  return {
    ...detail,
    evidenceDetails,
    evidenceQuotes,
    sourceContext: detail.sourceContext
      ? {
          ...detail.sourceContext,
          contextTurns: detail.sourceContext.contextTurns ?? [],
        }
      : null,
    lifeEvents: detail.lifeEvents ?? [],
    confidenceTrajectory: detail.confidenceTrajectory ?? [],
    relatedConflicts: detail.relatedConflicts ?? [],
    reinforcementTurns: detail.reinforcementTurns ?? [],
    pipelineGates: detail.pipelineGates ?? [],
    editHistory: detail.editHistory ?? [],
    claim: {
      ...detail.claim,
      fullPayload: detail.claim.fullPayload ?? {},
    },
  };
}

const ClaimDetailDrawer = ({
  open,
  detail,
  userId,
  accessToken,
  clientDemoMode = false,
  onClose,
  onOpenClaim,
  onViewInConversation,
  onClaimUpdated,
}: ClaimDetailDrawerProps) => {
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [payloadExpanded, setPayloadExpanded] = useState(false);
  const [provenanceExpanded, setProvenanceExpanded] = useState(false);
  const [pipelineExpanded, setPipelineExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [eventHistoryExpanded, setEventHistoryExpanded] = useState(false);
  const [editHistoryExpanded, setEditHistoryExpanded] = useState(false);

  useEffect(() => {
    if (!open) {
      setSummaryExpanded(false);
      setPayloadExpanded(false);
      setProvenanceExpanded(false);
      setPipelineExpanded(false);
      setHistoryExpanded(false);
      setEventHistoryExpanded(false);
      setEditHistoryExpanded(false);
    }
  }, [open]);

  useEffect(() => {
    setSummaryExpanded(false);
    setPayloadExpanded(false);
    setProvenanceExpanded(false);
    setPipelineExpanded(false);
    setHistoryExpanded(false);
    setEventHistoryExpanded(false);
    setEditHistoryExpanded(false);
  }, [detail?.claim.id]);

  const normalizedDetail = detail ? normalizeClaimDetail(detail) : null;
  const claim = normalizedDetail?.claim;
  const curationEditHistory =
    normalizedDetail?.editHistory.filter((e) => CURATION_EVENT_TYPES.has(e.eventType)) ?? [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 2, pb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Claim detail
        </Typography>

        {!normalizedDetail && <CircularProgress size={24} />}

        {normalizedDetail && claim && (
          <>
            <CollapsibleSection
              title="Summary"
              expanded={summaryExpanded}
              onToggle={() => setSummaryExpanded((v) => !v)}
              first
            >
              <Typography variant="subtitle1">
                {claim.predicate}="{claim.value}"
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                <ProvenanceChip provenance={claim.provenance} />
                <Chip size="small" variant="outlined" label={DIMENSION_LABELS[claim.dimension]} />
                <Chip size="small" label={`conf ${claim.confidence.toFixed(2)}`} />
                <Chip size="small" variant="outlined" label={claim.status} />
                {claim.lastSeenTurn != null && (
                  <Chip size="small" variant="outlined" label={`last seen turn ${claim.lastSeenTurn}`} />
                )}
              </Stack>
            </CollapsibleSection>

            <CollapsibleSection
              title="What it says"
              expanded={payloadExpanded}
              onToggle={() => setPayloadExpanded((v) => !v)}
            >
              {clientDemoMode ? (
                <Typography variant="body2" color="text.secondary">
                  Structured claim payload is hidden in client-demo mode.
                </Typography>
              ) : (
                <Box
                  component="pre"
                  sx={{
                    fontSize: '0.7rem',
                    bgcolor: 'action.hover',
                    p: 1.5,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 280,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(normalizedDetail.claim.fullPayload, null, 2)}
                </Box>
              )}
            </CollapsibleSection>

            <Divider sx={{ my: 2 }} />

            <CollapsibleSection
              title="Where it came from"
              expanded={provenanceExpanded}
              onToggle={() => setProvenanceExpanded((v) => !v)}
            >
              {normalizedDetail.sourceTurnId && !clientDemoMode && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Source turn: {normalizedDetail.sourceTurnId}
                </Typography>
              )}
              {normalizedDetail.sourceContext?.chatId && onViewInConversation && (
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  sx={{ mt: 0.5, px: 0 }}
                  onClick={() => onViewInConversation(normalizedDetail.sourceContext!.chatId)}
                >
                  View in conversation
                </Button>
              )}

              {normalizedDetail.sourceContext && normalizedDetail.sourceContext.contextTurns.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Surrounding context
                  </Typography>
                  {normalizedDetail.sourceContext.contextTurns.map((turn) => (
                    <Box
                      key={`${turn.role}-${turn.turnIndex}`}
                      sx={{
                        mb: 1,
                        p: 1,
                        borderRadius: 1,
                        border: 1,
                        borderColor: turn.role === 'source' ? 'primary.main' : 'divider',
                        bgcolor: turn.role === 'source' ? 'action.selected' : 'transparent',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {turn.role === 'source' ? 'Source turn' : turn.role === 'before' ? 'Previous turn' : 'Next turn'}{' '}
                        · turn {turn.turnIndex}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>User:</strong> {turn.userText}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Synapse:</strong> {turn.assistantText.slice(0, 400)}
                        {turn.assistantText.length > 400 ? '…' : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5, mb: 0.5 }}>
                Evidence spans
              </Typography>
              {normalizedDetail.evidenceDetails.length > 0 ? (
                normalizedDetail.evidenceDetails.map((evidence, i) => (
                  <Box key={i} sx={{ mb: 1 }}>
                    <Chip size="small" label={evidence.spanRole} variant="outlined" sx={{ mb: 0.5 }} />
                    <Typography variant="body2" sx={{ fontStyle: 'italic', pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                      "{evidence.quote}"
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No evidence span recorded
                </Typography>
              )}
            </CollapsibleSection>

            {!clientDemoMode && (
              <>
                <Divider sx={{ my: 2 }} />

                <CollapsibleSection
                  title="How it got here"
                  expanded={pipelineExpanded}
                  onToggle={() => setPipelineExpanded((v) => !v)}
                >
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Pipeline gates
                  </Typography>
                  {normalizedDetail.pipelineGates.map((gate) => (
                    <Typography key={gate.name} variant="caption" display="block" sx={{ mt: 0.25 }}>
                      {gate.passed ? '✓' : '✗'} {gate.name}: {gate.detail}
                      {!gate.logged && ' (not logged)'}
                    </Typography>
                  ))}
                </CollapsibleSection>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <CollapsibleSection
              title="What's happened since"
              expanded={historyExpanded}
              onToggle={() => setHistoryExpanded((v) => !v)}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Confidence trajectory
              </Typography>
              <ConfidenceTrajectoryChart points={normalizedDetail.confidenceTrajectory} />

              {normalizedDetail.lifeEvents.length > 0 && (
                <CollapsibleSection
                  title={`Event history (${normalizedDetail.lifeEvents.length})`}
                  expanded={eventHistoryExpanded}
                  onToggle={() => setEventHistoryExpanded((v) => !v)}
                  nested
                >
                  <List dense disablePadding>
                    {normalizedDetail.lifeEvents.map((event, index) => (
                      <ListItem key={`${event.timestamp}-${index}`} disableGutters sx={{ alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Chip size="small" label={LIFE_EVENT_LABELS[event.kind] ?? event.kind} variant="outlined" />
                              <Typography variant="caption" color="text.secondary">
                                {new Date(event.timestamp).toLocaleString()}
                                {event.turnIndex != null ? ` · turn ${event.turnIndex}` : ''}
                              </Typography>
                            </Stack>
                          }
                          secondary={
                            <>
                              {event.summary}
                              {event.confidenceDelta != null && (
                                <> · Δ {event.confidenceDelta > 0 ? '+' : ''}{event.confidenceDelta.toFixed(2)}</>
                              )}
                              {event.confidenceAfter != null && <> → conf {event.confidenceAfter.toFixed(2)}</>}
                              {event.detail && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {event.detail}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CollapsibleSection>
              )}

              {normalizedDetail.relatedConflicts.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 0.5 }}>
                    Related conflicts
                  </Typography>
                  {normalizedDetail.relatedConflicts.map((conflict) => (
                    <Box key={conflict.conflictId} sx={{ mb: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={conflict.status} color={conflict.status === 'pending' ? 'warning' : 'default'} />
                        <Typography variant="caption">{conflict.reason}</Typography>
                      </Stack>
                      {onOpenClaim && (
                        <Button size="small" sx={{ px: 0, mt: 0.25 }} onClick={() => onOpenClaim(conflict.otherClaimId)}>
                          View {conflict.role === 'existing' ? 'candidate' : 'existing'} claim
                        </Button>
                      )}
                    </Box>
                  ))}
                </>
              )}
              {curationEditHistory.length > 0 && (
                <CollapsibleSection
                  title={`Edit history (${curationEditHistory.length})`}
                  expanded={editHistoryExpanded}
                  onToggle={() => setEditHistoryExpanded((v) => !v)}
                  nested
                >
                  <ClaimEditHistoryList entries={curationEditHistory} />
                </CollapsibleSection>
              )}
            </CollapsibleSection>

            {userId && onClaimUpdated && !clientDemoMode && (
              <ClaimCurationPanel
                userId={userId}
                detail={normalizedDetail}
                accessToken={accessToken}
                onUpdated={onClaimUpdated}
              />
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default ClaimDetailDrawer;
